import { useCallback, useEffect, useRef, useState } from "react";
import { useAuth } from "@clerk/clerk-react";
import { useToast } from "../../../components/ui";
import { ApiError } from "../../../lib/http";
import { saveDietitianProfile } from "../services/settingsService";
import type { DietitianProfile } from "../types/settings.types";

export interface ProfileSave {
  /** True while the call is in flight; the form's buttons read it. */
  saving: boolean;
  /**
   * Sends what `next` changed against `saved`, and answers with the profile as
   * the backend now holds it — or `null` where the call failed, which the form
   * takes as "stay dirty, keep what was typed, and let Save try again".
   */
  save: (
    next: DietitianProfile,
    saved: DietitianProfile
  ) => Promise<DietitianProfile | null>;
}

const failureMessage = (error: unknown) =>
  error instanceof ApiError
    ? error.message
    : "We could not reach the server. Check your connection and try again.";

/**
 * Saves the General tab, and says how it went.
 *
 * One call, unlike the My brand screen's two — `/api/v1/settings/` writes both
 * records behind this form — so there is no half to report separately: it
 * either landed or it did not.
 *
 * Must be used inside a Clerk provider and an active session.
 */
export function useProfileSave(): ProfileSave {
  const { getToken } = useAuth();
  const toast = useToast();

  // Clerk hands back a fresh `getToken` on some renders, and this hook is used
  // by a form that re-renders on every keystroke — so `save` reads the current
  // one through a ref rather than depending on its identity.
  const getTokenRef = useRef(getToken);
  useEffect(() => {
    getTokenRef.current = getToken;
  }, [getToken]);

  const [saving, setSaving] = useState(false);

  // Nothing to abort on unmount: a save already with the server should finish.
  // This only stops the result being written back into a gone form.
  const mounted = useRef(true);
  useEffect(() => {
    mounted.current = true;
    return () => {
      mounted.current = false;
    };
  }, []);

  const save = useCallback(
    async (
      next: DietitianProfile,
      saved: DietitianProfile
    ): Promise<DietitianProfile | null> => {
      setSaving(true);

      try {
        const stored = await saveDietitianProfile(
          () => getTokenRef.current(),
          next,
          saved
        );

        toast.success("Profile updated", "Your details are up to date.");
        return stored;
      } catch (error) {
        console.error("profile failed to save:", error);
        toast.error("Could not save your profile", failureMessage(error));
        return null;
      } finally {
        if (mounted.current) setSaving(false);
      }
    },
    [toast]
  );

  return { saving, save };
}

export default useProfileSave;
